import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { SendCodeEmailRequestDTO } from '@/core/api/dtos/request/zone/sendCodeEmail'
import { Zone } from '@/core/api/entities/zone/zone'
import { useSendCodeEmail } from '@/core/hooks/useZone'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseZoneSendCodeEmailProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
}

export const useZoneSendCodeEmail = (props?: UseZoneSendCodeEmailProps) => {
	const { onSuccess, onError } = props || {}
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { mutate, isPending } = useSendCodeEmail({
		options: {
			onSuccess: (
				_: void,
				variables: { zoneID: number; data: SendCodeEmailRequestDTO }
			) => {
				// Show success notification
				showNotification({
					type: 'success',
					message: t('entities.zone.email_code.success.title'),
					description: t('entities.zone.email_code.success.description', {
						email: variables.data.email,
					}),
				})

				// Close modal
				setIsModalVisible(false)
				setSelectedZone(null)

				// Call onSuccess callback if provided
				if (onSuccess) onSuccess()
			},
			onError: (error: Error) => {
				// Show error notification
				showNotification({
					type: 'error',
					message: t('entities.zone.email_code.error.title'),
					description: t('entities.zone.email_code.error.description'),
				})

				// Call onError callback if provided
				if (onError) onError(error)
			},
		},
	})

	const openSendCodeEmailModal = (zone: Zone) => {
		setSelectedZone(zone)
		setIsModalVisible(true)
	}

	const closeSendCodeEmailModal = () => {
		setIsModalVisible(false)
		setSelectedZone(null)
	}

	const handleSendCodeEmail = (data: SendCodeEmailRequestDTO) => {
		if (selectedZone?.id) {
			mutate({
				zoneID: Number(selectedZone.id),
				data,
			})
		}
	}

	return {
		selectedZone,
		isModalVisible,
		isSending: isPending,
		openSendCodeEmailModal,
		closeSendCodeEmailModal,
		handleSendCodeEmail,
	}
}
